import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { getApi } from "../../utils/api";
import {
  API_DOWNLOAD_SALARY,
  API_GET_REIMBURSEMENT_TYPES,
  API_REIMBURSEMENT_LIST,
} from "../../config/Endpoints";
import { ThreeDots } from "react-loader-spinner";
import ReimbursementWithdrawRequest from "./ReimbursementWithdrawRequest";
import Footer from "../../components/Footer";
import PageTitle from "../../components/PageTitle";
import { generateMonths, getYearsUpToCurrent } from "../../utils/helpers";
import { displayFormatDate } from "../../utils/helpers";
import PageShell from "../../v2/components/shared/page-shell/page-shell.tsx";
import { Card } from "../../v2/components/shared/card/card.tsx";
import {
  Badge,
  Button,
  ButtonLink,
  ResponsiveDataTable,
  Select,
} from "../../v2/components/ui/index.ts";
import ReimbursementAmount from "../../components/ReimbursementAmount";
import { useLoggedInEmployee } from "../../v2/hooks/use-logged-in-employee.ts";

// Data table
// import DataTable from "react-data-table-component";

const ReimbursementRequestList = ({ title, description }) => {
  const { isUs } = useLoggedInEmployee();
  const [reimbursementList, setReimbursementList] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isReimbursementRequest, setIsReimbursementRequest] = useState(false);
  const accessToken = Cookies.get("accessToken");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "",
  });
  const [reimbursementId, setReimbursementId] = useState("");
  const [selectedReimbursementRequest, setSelectedReimbursementRequest] =
    useState({});
  const [reimbursementTypes, setReimbursementTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalReimbursement, setTotalReimbursement] = useState(0);
  const [months, setMonths] = useState(generateMonths());
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const years = getYearsUpToCurrent(2020).map((year) => ({
    value: String(year),
    label: String(year),
  }));

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };
  const columns = [
    {
      key: "reimbursementType",
      label: "Reimbursement Type",
    },
    {
      key: "requestDate",
      label: "Requested Date",
      render: (row) => displayFormatDate(row.date),
      compare: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      key: "date",
      label: "Invoice Date",
      render: (row) => displayFormatDate(row.date),
      compare: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      key: "invoiceNo",
      label: "Invoice No.",
    },
    {
      key: "description",
      label: "Description",
      render: (row) => row.description?.trim() || "-",
    },
    {
      key: "reimbursementAmount",
      label: "Amount",
      render: (row) => (
        <ReimbursementAmount
          amount={row.reimbursementAmount.toLocaleString("en-IN")}
          currencyType={row.currencyType}
        />
      ),
      compare: (a, b) => a.reimbursementAmount - b.reimbursementAmount,
    },
    {
      key: "trackingStatus",
      label: "Status",
    },
    {
      key: "action",
      label: "Action",
      sortable: false,
      render: (row) =>
        row.trackingStatus === "Discrepancy" ||
          row.trackingStatus === "Pending-Admin" ? (
          <button
            data-bs-toggle="modal"
            data-bs-target="#staticBackdropAssetwithdraw"
            className="action-btn action-btn--withdraw"
            onClick={() => {
              setReimbursementId(row.reimbursementId);
              setSelectedReimbursementRequest(row);
            }}
          >
            Withdraw
          </button>
        ) : (
          <p className="ms-2">-</p>
        ),
    },
  ].filter(
    (column) =>
      !isUs || !["requestDate", "date", "invoiceNo"].includes(column.key),
  );

  const tableColumns = [
    {
      id: "reimbursementType",
      header: "Reimbursement Type",
      accessor: "reimbursementType",
      sortable: true,
      truncate: true,
      getTitleText: (row) => String(row.reimbursementType ?? ""),
    },
    {
      id: "requestDate",
      header: "Requested Date",
      accessor: "requestDate",
      render: (row) => displayFormatDate(row.date),
      compare: (a, b) => new Date(a.date) - new Date(b.date),
      sortable: true,
    },
    {
      id: "date",
      header: "Invoice Date",
      sortable: true,
      getSortValue: (row) => new Date(row.date).getTime(),
      render: (row) => displayFormatDate(row.date),
    },
    {
      id: "invoiceNo",
      header: "Invoice No.",
      accessor: "invoiceNo",
      sortable: true,
      truncate: true,
      getTitleText: (row) => String(row.invoiceNo ?? ""),
    },
    {
      id: "description",
      header: "Description",
      sortable: true,
      getSortValue: (row) => String(row.description ?? ""),
      render: (row) => row.description?.trim() || "-",
      truncate: true,
      getTitleText: (row) => String(row.description ?? ""),
    },
    {
      id: "reimbursementAmount",
      header: "Amount",
      sortable: true,
      getSortValue: (row) => row.reimbursementAmount,
      render: (row) => (
        <ReimbursementAmount
          amount={Number(row.reimbursementAmount || 0).toLocaleString("en-IN")}
          currencyType={row.currencyType}
        />
      ),
    },
    {
      id: "trackingStatus",
      header: "Status",
      accessor: "trackingStatus",
      sortable: true,
      nowrap: true,
      render: (row) => (
        <Badge
          variant={
            row.trackingStatus === "Approved"
              ? "success"
              : row.trackingStatus === "Rejected"
                ? "danger"
                : "warning"
          }
        >
          {row.trackingStatus}
        </Badge>
      ),
    },
    {
      id: "action",
      header: "Action",
      sortable: false,
      render: (row) =>
        row.trackingStatus === "Discrepancy" ||
          row.trackingStatus === "Pending-Admin" ? (
          <button
            data-bs-toggle="modal"
            data-bs-target="#staticBackdropAssetwithdraw"
            className="action-btn action-btn--withdraw"
            onClick={() => {
              setReimbursementId(row.reimbursementId);
              setSelectedReimbursementRequest(row);
            }}
          >
            Withdraw
          </button>
        ) : (
          <span className="ms-2">-</span>
        ),
    },
  ].filter(
    (column) =>
      !isUs || !["requestDate", "date", "invoiceNo"].includes(column.id),
  );

  useEffect(() => {
    fetchList();
  }, [currentPage, limit]);

  useEffect(() => {
    getApi(API_GET_REIMBURSEMENT_TYPES, accessToken)
      .then((res) => {
        if (res.status === 200) {
          const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
          const nestedTypes = Array.isArray(rows?.[0]?.reimbursementType)
            ? rows[0].reimbursementType
            : [];
          const flatTypes = rows
            .map((item) => item?.reimbursemnetType || item?.reimbursementType)
            .filter((item) => typeof item === "string");
          const unique = [...new Set([...nestedTypes, ...flatTypes])];
          const types = unique.map((type) => ({ value: type, label: type }));
          setReimbursementTypes(types);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    let filteredMonths = generateMonths();
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (selectedYear && Number(selectedYear.value) === currentYear) {
      filteredMonths = filteredMonths.filter(
        (month) => parseInt(month.value, 10) <= currentMonth
      );
    }

    setMonths(filteredMonths);
  }, [selectedYear]);

  useEffect(() => {
    if (!selectedYear && !selectedMonth && !selectedType) {
      fetchList();
    }
  }, [selectedYear, selectedMonth, selectedType]);

  const handleLimitChange = (e) => {
    setLimit(e.target.value);
    setCurrentPage(1);
  };

  const fetchList = () => {
    setIsLoading(true);

    let query = `?page=${currentPage}&pageSize=${limit}&`;

    if (selectedYear) query += `year=${selectedYear.value}&`;
    if (selectedMonth) query += `month=${selectedMonth.value}&`;
    if (selectedType)
      query += `reimbursementType=${encodeURIComponent(selectedType.value)}&`;

    query = query.slice(0, -1);

    getApi(API_REIMBURSEMENT_LIST + query, accessToken)
      .then((res) => {
        if (res.status === 200) {
          setReimbursementList(res.data.data);
          setFilteredItems(res.data.data);
          setTotalReimbursement(res.data.totalRequest);
          setTotalPages(res.data.totalPages);
        }
        setIsLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
      });
  };

  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    const filtered = reimbursementList.filter((item) =>
      item.reimbursementType.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredItems(filtered);
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    const sortedData = [...filteredItems].sort((a, b) => {
      if (key === "requestDate" || key === "date") {
        const dateA = new Date(a[key]);
        const dateB = new Date(b[key]);
        if (dateA < dateB) {
          return direction === "ascending" ? -1 : 1;
        }
        if (dateA > dateB) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      } else {
        if (a[key] < b[key]) {
          return direction === "ascending" ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      }
    });

    setFilteredItems(sortedData);
  };

  return (
    <PageShell>


      <Card className="mb-3" padding="3xl">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <Select
              label="Reimbursement Type"
              options={reimbursementTypes}
              value={selectedType?.value ?? ""}
              onChange={(value) => {
                setSelectedType(
                  reimbursementTypes.find((item) => item.value === value) ?? null,
                );
              }}
              placeholder="Select Reimbursement Type"
              isClearable
              searchable
            />
          </div>
          <div className="col-12 col-md-3">
            <Select
              label="Year"
              options={years}
              value={selectedYear?.value != null ? String(selectedYear.value) : ""}
              onChange={(value) => {
                const nextYear =
                  years.find((year) => year.value === value) ?? null;
                setSelectedYear(nextYear);
                setSelectedMonth(null);
              }}
              placeholder="Select Year"
              isClearable
            />
          </div>

          {selectedYear && (
            <div className="col-12 col-md-3">
              <Select
                label="Month"
                options={months}
                value={selectedMonth?.value ?? ""}
                onChange={(value) => {
                  const nextMonth =
                    months.find((month) => month.value === value) ?? null;
                  setSelectedMonth(nextMonth);
                }}
                placeholder="Select Month"
                isClearable
              />
            </div>
          )}

          <div className="col-12 col-md-auto ms-md-auto d-flex gap-2 flex-nowrap justify-content-end align-items-end">
            <Button variant="accent" onClick={fetchList}>
              Filter
            </Button>
            <ButtonLink
              to="/dashboard/createReimbursementRequest"
              variant="muted-outline"
            >
              Reimbursement Request
            </ButtonLink>
          </div>
        </div>
      </Card>

      <Card padding="3xl">


        {isLoading ? (
          <div className="d-flex justify-content-center py-5">
            <ThreeDots
              height="80"
              width="80"
              radius="9"
              color="var(--color-accent-500)"
              ariaLabel="three-dots-loading"
              visible={true}
            />
          </div>
        ) : filteredItems?.length > 0 ? (
          <ResponsiveDataTable
            columns={tableColumns}
            data={filteredItems}
            aria-label="Reimbursement request list"
            serialNumber
            page={currentPage}
            pageSize={Number(limit)}
            totalItems={totalReimbursement}
            onPageChange={handlePageChange}
            onPageSizeChange={(size) => {
              setLimit(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[5, 10, 20, 30, 40]}
            totalLabel={
              <>
                Total Requests: <strong>{totalReimbursement}</strong>
              </>
            }
          />
        ) : (
          <div className="fs-4 text-secondary text-center">
            <h4>No Record Available</h4>
          </div>
        )}
      </Card>

      <div
        className="modal fade"
        id="staticBackdropAssetwithdraw"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-labelledby="staticBackdropLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <ReimbursementWithdrawRequest
              reimbursementId={reimbursementId}
              selectedReimbursementRequest={selectedReimbursementRequest}
              fetchList={fetchList}
            />
          </div>
        </div>
      </div>
      {/* <Footer /> */}
    </PageShell>
  );
};

export default ReimbursementRequestList;
